import { UsePDFInstance, pdf } from "@react-pdf/renderer";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import AppointmentService from "../../api/AppointmentService";
import JobService from "../../api/JobService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import {
  CustomEmailInput,
  EmailProps,
} from "../../components/CustomEmailInput/CustomEmailInput";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { Info } from "../../components/Info/Info";
import Modal from "../../components/Modal/Modal";
import { DeficiencyPDFDocumentForm } from "../../components/PDFDocument/DeficiencyPDFDocumentForm";
import { PDFDocumentForm } from "../../components/PDFDocument/PDFDocumentForm";
import { SignatureCanvas } from "../../components/SignatureCanvas/SignatureCanvas";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useHandleService } from "../../hooks/useHandleService";
import { useService } from "../../hooks/useService";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { appointmentPropertyAreasActions } from "../../store/appointment-property-areas-slice";
import { resetState } from "../../store/appointment-slice";
import { clockActions } from "../../store/clock-slice";
import { deficiencyServiceActions } from "../../store/deficiency-service-slice";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import { oldMonitorsActions } from "../../store/old-monitors-slice";
import { productSliceActions } from "../../store/products-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/services-completion.module.css";
import { calculateTotalTime } from "../../utils/calculateTotalTime";
import { formatWorkloadTime } from "../../utils/formatWorkloadTime";
import { parseAddress } from "../../utils/parseAddress";

export const ServicesCompletion = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [showNoSignatureContent, setNoSignatureContent] = useState(false);
  const [textArea, setTextArea] = useState("");
  const [emails, setEmails] = useState<EmailProps[]>([]);
  const [totalTime, setTotalTime] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const appointment = useSelector((state: RootState) => state.appointment);
  const deficiencyAppointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );
  const documentation = useSelector((state: RootState) => state.documentation);

  const servicedAppointment =
    documentation.defAppointmentId !== undefined
      ? deficiencyAppointment
      : appointment;

  const { savePDF, updatePDF } = useDocumentation();
  const { street, postcode, city } = parseAddress(
    appointment.additionalData.property.address
  );

  const date = moment().format("DD.MM.YYYY");
  const currentTime = moment();
  const jobId = appointment?.jobs?.slice(-1).pop().id;
  const defJobId = documentation.defJobId;

  const servicedJob =
    documentation.defAppointmentId !== undefined ? defJobId : jobId;

  const { handleStartService } = useHandleService();
  const { startService } = useService(deficiencyAppointment);

  const isDeficiencyPDF = documentation.deficiencyPDF.deficiencyType !== null;

  const isCustomerDeficiency =
    documentation.deficiencyPDF.responsibility === "customer";

  useEffect(() => {
    if (documentation.defAppointmentId === undefined) {
      const appointmentSubsetData = {
        customerName: appointment.additionalData.customer.name,
        address: appointment.additionalData.property.address,
        street: street ?? "",
        postcode: postcode ?? "",
        city: city ?? "",
        objectName: appointment.additionalData.property.name ?? null,
        technicians: appointment.technicians.map(({ firstName, lastName }) => ({
          firstName,
          lastName,
        })),
        typeOfAppointment: appointment.typeOfAppointment,
      };

      dispatch(
        creatingDocumentationActions.setAppointmentSubset(appointmentSubsetData)
      );
      const plannedStart = moment(appointment.plannedStart);
      dispatch(
        creatingDocumentationActions.setDate(
          plannedStart.isBefore(moment())
            ? plannedStart.format("DD. MMMM YYYY")
            : moment().format("DD. MMMM YYYY")
        )
      );
    }
  }, [dispatch, appointment]);

  const fetchTotalTime = useCallback(async () => {
    if (isCustomerDeficiency) {
      return;
    }

    try {
      const { totalTime } = await calculateTotalTime(servicedAppointment.id);

      setTotalTime(totalTime);

      dispatch(
        creatingDocumentationActions.setTotalTimeWorkload(
          formatWorkloadTime(totalTime)
        )
      );
    } catch (error) {
      console.error(error);
    }
  }, [isCustomerDeficiency, servicedAppointment.id, dispatch]);

  const fetchCustomerEmails = useCallback(async () => {
    try {
      let propertyUsers: any[] = [];
      try {
        const propertyResponse =
          await PropertyService.GetPropertyAccessibleUsers(
            servicedAppointment.propertyId
          );
        propertyUsers = propertyResponse?.data ?? [];
      } catch (err) {
        propertyUsers = [];
      }

      const propertyEmails = propertyUsers
        .filter((user: any) => !!user.email)
        .map((user: any) => ({
          id: uuidv4(),
          email: user.email,
        }));

      const uniqueEmailValues: EmailProps[] = Array.from(
        new Map<string, EmailProps>(
          [...propertyEmails].map((e) => [e.email, e])
        ).values()
      );

      setEmails(uniqueEmailValues);

      const existingEmails = new Set(
        (documentation.emails ?? []).map((e: EmailProps) => e.email)
      );

      uniqueEmailValues.forEach((email) => {
        if (!existingEmails.has(email.email)) {
          dispatch(creatingDocumentationActions.addEmail(email));
        }
      });
    } catch (error) {
      console.error(error);
    }
  }, [servicedAppointment.propertyId, documentation.emails, dispatch]);

  useEffect(() => {
    fetchTotalTime();
    fetchCustomerEmails();
  }, [servicedAppointment.id]);

  const handleNoSignatureClick = () => {
    setNoSignatureContent(true);
  };

  const returnToDashboard = async () => {
    setIsModalOpen(false);
    setLoading(true);

    try {
      await generatePdfDocument();

      const updateRequests = [];

      if (servicedJob && !isCustomerDeficiency) {
        updateRequests.push(
          JobService.UpdateJob(servicedJob, {
            jobState: "processed",
          })
        );
      }

      if (!isCustomerDeficiency) {
        updateRequests.push(
          AppointmentService.UpdateAppointment(servicedAppointment.id, {
            appointmentState: "completed",
          })
        );
      }

      await Promise.all(updateRequests);

      if (documentation.emails.length > 0 && !isCustomerDeficiency) {
        await AppointmentService.SendEmailsForCompletedAppointment(
          servicedAppointment.id,
          {
            emails: documentation.emails.map((e) => e.email),
          }
        );
      }

      await Promise.all([
        dispatch(activeTabsActions.setMonitorsStep(1)),
        dispatch(activeTabsActions.setServicesStep(1)),
        dispatch(creatingDocumentationActions.resetState()),
        dispatch(deficiencyServiceActions.resetState()),
        dispatch(productSliceActions.resetState()),
        dispatch(
          oldMonitorsActions.removeMonitorsByAppointment(servicedAppointment.id)
        ),
        dispatch(servicedMonitorsActions.resetState()),
        dispatch(appointmentPropertyAreasActions.resetState()),
        dispatch(clockActions.resetClock(servicedAppointment.id)),
      ]);

      dispatch(activeTabsActions.setDashbaordUrl(""));
      dispatch(resetState());

      navigate("/");
    } catch (error) {
      console.error("An error occurred while completing the service", error);
      setError("Beenden des Dienstes fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    if (textArea.length > 0) {
      returnToDashboard();
    } else {
      setNoSignatureContent(false);
    }
  };

  const handleTextareaChange = (note: string) => {
    setTextArea(note);
  };

  const handleNameChange = (name: string) => {
    dispatch(creatingDocumentationActions.setEmailOwner(name));
  };

  const handleEmailInputChange = useCallback(
    (email: EmailProps) => {
      dispatch(creatingDocumentationActions.addEmail(email));
    },
    [dispatch]
  );

  const handleBackButtonClicked = () => {
    dispatch(creatingDocumentationActions.resetEmails());

    const hasDefAppointment = documentation.defAppointmentId !== undefined;

    if (hasDefAppointment) {
      startService();
    } else if (!isCustomerDeficiency && !hasDefAppointment) {
      handleStartService();
    }

    goOneStepBack(navigate);
  };

  const handleBackButtonClickedOnModal = () => {
    setNoSignatureContent(false);
    setTextArea("");
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const saveReceipt = async (instance: UsePDFInstance) => {
    if (instance.loading || !instance.url || !instance.blob) {
      return;
    }

    try {
      if (!documentation.existingReceipt) {
        await savePDF(instance, documentation.attachedTo);
      } else {
        await updatePDF(instance);
      }
    } catch (error) {
      setError("Fehler beim Speichern der PDF-Datei.");
      throw error;
    }
  };

  const generatePdfDocument = async () => {
    try {
      const PdfComponent = isDeficiencyPDF
        ? DeficiencyPDFDocumentForm
        : PDFDocumentForm;

      const blob = await pdf(
        <PdfComponent documentation={documentation} />
      ).toBlob();

      if (!blob) {
        throw new Error("PDF generation failed. Blob is empty.");
      }

      const instance: UsePDFInstance = {
        blob,
        loading: false,
        url: "PDF document",
        error: null,
      };
      await saveReceipt(instance);
    } catch (error) {
      setError(
        "Beim Hochladen der PDF-Datei ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."
      );
      throw new Error("PDF generation or upload failed."); // Rethrow to handle it in `returnToDashboard`
    }
  };

  return (
    <AppLayout hideHeader>
      <Modal
        isOpen={isModalOpen}
        message={t("ServiceCompletionConfirmation", {
          date: date,
          time: currentTime.format("HH:mm"),
        })}
        totalTimeTracking={t("TotalWorkingTime", {
          totalTime: formatWorkloadTime(totalTime),
        })}
        note={"EndingOfTimeMeasurement"}
        onConfirm={returnToDashboard}
        onCancel={handleCloseModal}
        cancelText={"Cancel"}
        confirmText={"Confirm"}
        backButtonShow={handleCloseModal}
      />

      <div>
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          showDocumentation={false}
        />

        <AppointmentDetails
          isServicesCompletion
          deficiency={documentation.defAppointmentId !== undefined}
          showMangelTag={documentation.deficiencyPDF.deficiencyType !== null}
          hideClock={
            documentation.deficiencyPDF.responsibility === "customer"
              ? true
              : false
          }
          deficiencyDate={
            isCustomerDeficiency ? documentation?.deficiencyPDF.time : undefined
          }
        />

        <div className={styles["content-wrapper"]}>
          <div className={styles["content"]}>
            <span className={styles["title"]}>Dokumentation</span>
            <CustomInput
              label="Auftraggeber/in"
              placeholder="Verantwortlichen eintragen"
              onInputChange={handleNameChange}
              hideTick
              maxLength={50}
            />

            <SignatureCanvas />

            <span
              className={styles["no-signature"]}
              onClick={handleNoSignatureClick}
            >
              {t("No signature available")}
            </span>

            <CustomEmailInput
              initialValues={emails}
              placeholder={t("EnterEmail") ?? ""}
              onInputChange={handleEmailInputChange}
            />

            <Info type="success">{t("PDFInfo")}</Info>
            <FloatingButtons>
              <CustomButton
                color="green"
                onClick={handleOpenModal}
                width={80}
                loading={loading}
              >
                {t("CompleteService")}
              </CustomButton>
            </FloatingButtons>
          </div>
        </div>
      </div>
      {showNoSignatureContent && (
        <div className={styles["no-signature-overlay"]}>
          <BackButtonHeader
            handleBackButtonClicked={handleBackButtonClickedOnModal}
          />

          <div className={styles["no-signature-content"]}>
            <span className={styles["no-signature__title"]}>
              {t("A signature from the client is not possible")}
            </span>

            <CustomTextArea
              placeholder={t("AdditionalText")}
              color="gray"
              onTextareaChange={handleTextareaChange}
              value={textArea}
            />

            <div
              className={`${styles["button-wrapper"]} ${
                !(textArea.length > 0) && styles["button-wrapper--grayed-out"]
              }`}
            >
              <CustomButton color="green" onClick={handleClosePopup} width={60}>
                {textArea.length > 0 ? "Bestätigen" : "abbrechen"}
              </CustomButton>
            </div>
          </div>
          {!loading && error.length > 0 && <Info type="error">{error}</Info>}
        </div>
      )}
    </AppLayout>
  );
};
